import React, {type Dispatch, type SetStateAction, useCallback} from 'react';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';

import {CheckBox} from '@pb/kit-component-checkbox';
import {FormSection} from '@pb/kit-component-form-section';


import {PbWorkspaceSettingsSkeleton} from './pb-workspace-settings.skeleton';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {Error} from 'src/openapi/admin/models/Error';
import type {WorkspaceSettings} from 'src/openapi/admin/models/WorkspaceSettings';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    workspaceSettings: WorkspaceSettings,
    setWorkspaceSettings: Dispatch<SetStateAction<WorkspaceSettings>>,
    onFieldChange: (parameterName: string) => void,
    disabled?: boolean,
};

const SECTION_NAME = 'Workspaces Settings';

export const PbWorkspaceSettingsSection = (props: Props) => {
    const {
        isLoading,
        loadingError,
        workspaceSettings,
        setWorkspaceSettings,
        onFieldChange,
        disabled,
    } = props;

    const {hasAccess} = useAdminAccessRight();

    const changeWorkspaceSettings = useCallback(({event}: CheckBoxChangeData) => {
        setWorkspaceSettings({publishAllowed: event.currentTarget.checked});
        onFieldChange('Publish PitchBook Workspaces');
    }, [onFieldChange, setWorkspaceSettings]);

    return (
        <Skeleton shown={isLoading} view={<PbWorkspaceSettingsSkeleton/>}>
            <ServerResponseHandler
                smallView={true}
                sectionName={SECTION_NAME}
                serverError={loadingError}
                accessRight={ACCESS_RIGHTS.USER_SCREEN_VIEW as AccessRight}>
                <FormSection headline={SECTION_NAME}>
                    <CheckBox
                        disabled={!hasAccess(ACCESS_RIGHTS.PUBLISH_WORKSPACES) || disabled}
                        label="Publish PitchBook Workspaces"
                        checked={workspaceSettings.publishAllowed}
                        onCheckBoxChange={changeWorkspaceSettings}
                    />
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
